<script setup lang="ts">
import { IconExclamationCircle, IconHeartHandshake } from '@tabler/icons-vue'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import BaseButton from '@tix/ds/components/atoms/BaseButton.vue'
import StatusMessage from '@tix/ds/components/atoms/StatusMessage.vue'
import { Event } from '@tix/entity'
import { isNotFoundError } from '@tix/openapi-sdk'

import { setDocumentTitle } from '@/helpers/documentTitle'
import { buildDonationCustomAmount, buildDonationPresets } from '@/helpers/donationHelpers'
import { emptyDonationOptionsValues, getDonationOptionsPresentation } from '@/helpers/donationSurvey'
import { formatEventGalleryImages } from '@/helpers/imageHelpers'
import { buildDonationsPaymentReturnUrl } from '@/helpers/paymentReturn'

import { errorTracker } from '@/services/errorTracking'

import { useGlobalError } from '@/stores/useGlobalErrorStore'

import CheckoutIdentityFields, { type CheckoutIdentityFieldsExposed } from '@/components/CheckoutIdentityFields.vue'
import ImageGallery from '@/components/ImageGallery.vue'
import DonationCartTotal from '@/components/donations/DonationCartTotal.vue'
import DonationHeader from '@/components/donations/DonationHeader.vue'
import DonationOptions, { type DonationOptionsExposed } from '@/components/donations/DonationOptions.vue'
import DonationWidget from '@/components/donations/DonationWidget.vue'
import PaymentComponent from '@/components/donations/PaymentComponent.vue'

import { useDonationCart } from '@/composables/useDonationCart'
import { useDonationGa4Tracking } from '@/composables/useDonationGa4Tracking'

const props = defineProps<{
  id?: string
}>()

const router = useRouter()
const globalError = useGlobalError()

const donationOptionsRef = ref<DonationOptionsExposed | null>(null)
const donationOptions = ref(emptyDonationOptionsValues())
// Non-null assertion is safe: CheckoutIdentityFields mounts unconditionally inside `eventReady`, while PaymentComponent only appears after `paymentReady`, which requires user interaction to create a cart with stripeFee.
const checkoutIdentity = useTemplateRef<CheckoutIdentityFieldsExposed>('checkoutIdentity')

const eventTemplateId = props.id

const result = eventTemplateId ? await Event.load(eventTemplateId) : null

const event = result?.data ?? null
const eventTemplate = event?.data ?? null
const ticketGroups = event?.ticketGroups ?? []
const ticketTypes = event?.ticketTypes ?? []
const error = result?.error ?? null

// TODO: Implement global error-handling for all routes
if (!eventTemplate) {
  if (!eventTemplateId || isNotFoundError(error)) {
    globalError.set('Not found', { dismissable: false })
  } else {
    errorTracker.track(new Error('Error fetching donation event', { cause: error }), {
      operation: 'donation.event.get',
      eventTemplateId,
    })
    globalError.set('An unexpected error occurred', { dismissable: false })
  }
}

const donationGroup = ticketGroups.find((group) => group.handler === 'donation')
const donationTicketTypes = ticketTypes.filter((ticketType) => ticketType.ticket_group_id === donationGroup?.id)

const title = eventTemplate?.name ?? ''
const summary = eventTemplate?.summary ?? ''
const description = eventTemplate?.description ?? ''
const images = event ? formatEventGalleryImages(event.metadata) : []
const presetAmounts = buildDonationPresets(donationTicketTypes)
const customAmount = buildDonationCustomAmount(donationTicketTypes)

const hasDonationAmountOptions = presetAmounts.length > 0 || customAmount !== null

// The event loaded fine, but without a donation group or valid amounts there's
// nothing for the user to donate to. Treat it as not found rather than showing
// a header/gallery with no way to donate.
if (eventTemplate && !hasDonationAmountOptions) {
  globalError.set('Not found', { dismissable: false })
}

if (title) setDocumentTitle(title)

const donationOptionsPresentation = getDonationOptionsPresentation(donationGroup?.additional_info_spec)

const {
  cart,
  pending,
  error: cartError,
  selection,
  onSelectPreset,
  onCustomAmountUpdate,
  checkoutCart,
  checkoutPreflight,
  validateForm,
  amountInvalid,
} = useDonationCart(
  customAmount,
  donationOptionsPresentation && donationGroup ? { donationOptions, donationGroup } : undefined,
  checkoutIdentity,
  donationOptionsRef,
)

const eventReady = Boolean(eventTemplate) && hasDonationAmountOptions
const paymentReady = computed(() => eventReady && Boolean(cart.value?.stripeFee))
const isCartGone = computed(() => cartError.value === 'cart-gone')
/** True while StripePayment checkout is in flight — freezes the amount widget. */
const paymentSubmitting = ref(false)
// TODO: update label and icon in TIC-6842
const paymentButtonLabel = 'Complete donation'
const payButtonIcon = IconHeartHandshake
const preStripeError = ref<string | null>(null)

watch(paymentReady, (ready) => {
  if (!ready) return
  amountInvalid.value = false
  preStripeError.value = null
})

const { viewItem, beginCheckout, trackPurchase } = useDonationGa4Tracking({
  eventTemplate,
  donationGroup: donationGroup ?? null,
  ticketTypes: donationTicketTypes,
  paymentReady,
  cart,
  donationOptions,
})
viewItem()

function handlePaymentComplete(data: { email: string; order: string; amount: number }) {
  trackPurchase(data.order)
  router.push({
    name: 'donation-complete',
    params: { id: eventTemplateId },
    query: {
      email: data.email,
      order: data.order,
      // PaymentComponent emits Stripe minor units; the complete route/PriceDisplay expect major units.
      amount: String(data.amount / 100),
    },
  })
}

async function onPayWithoutPayment() {
  const validationError = validateForm()
  if (validationError === undefined) return

  if (validationError) {
    preStripeError.value = validationError
    return
  }

  // run remaining validation not handled by identity
  const preflight = await checkoutPreflight()
  if (preflight.status === 'error') {
    preStripeError.value = preflight.error.message
    return
  }

  amountInvalid.value = false
  preStripeError.value = null
}

/** Destination paths only. Cart id and receipt email are layered at Stripe confirm. */
const returnUrl = computed(() => buildDonationsPaymentReturnUrl(eventTemplateId!))
</script>

<template>
  <div v-if="eventReady" class="donation-page">
    <ImageGallery v-if="images.length" class="donation-page-gallery" :images="images" />

    <div class="donation-page-header">
      <DonationHeader :description="description" :name="title" :summary="summary" />
    </div>

    <aside class="donation-page-widget">
      <DonationWidget
        :customAmount="customAmount"
        :disabled="paymentSubmitting"
        :invalid="amountInvalid"
        :presetAmounts="presetAmounts"
        :selection="selection"
        @selectPreset="onSelectPreset"
        @update:customAmount="onCustomAmountUpdate"
      />
    </aside>

    <div class="donation-page-details">
      <DonationOptions
        v-if="donationOptionsPresentation"
        ref="donationOptionsRef"
        v-model="donationOptions"
        class="donation-page-options"
        :presentation="donationOptionsPresentation"
      />
      <CheckoutIdentityFields
        ref="checkoutIdentity"
        class="donation-page-identity"
        :disabled="paymentSubmitting"
        @focusin="beginCheckout"
      />
      <DonationCartTotal :cart="cart" class="donation-page-cart-total" :pending="pending" />
    </div>

    <StatusMessage
      v-if="isCartGone"
      :dismissible="false"
      :icon="IconExclamationCircle"
      message="Your donation session expired. Please select an amount again."
      variant="error"
    />

    <div v-else-if="paymentReady" class="donation-page-payment-container" @focusin="beginCheckout">
      <PaymentComponent
        :key="cart!.id"
        :cart="cart!"
        :checkoutCart="checkoutCart"
        :checkoutPreflight="checkoutPreflight"
        :getValidatedIdentity="checkoutIdentity!.getValidatedIdentity"
        :payButtonIcon="payButtonIcon"
        :paymentButtonLabel="paymentButtonLabel"
        :pending="pending"
        :resolveIdentityData="checkoutIdentity!.resolveIdentityData"
        :returnUrl="returnUrl"
        :validateForm="validateForm"
        @complete="handlePaymentComplete"
        @focus="beginCheckout"
        @update:submitting="paymentSubmitting = $event"
      />
    </div>

    <!-- Dummy button and status message handles validation before payment component mounts -->
    <div v-else class="donation-page-payment-container">
      <StatusMessage
        v-if="preStripeError"
        :dismissible="false"
        :icon="IconExclamationCircle"
        :message="preStripeError"
        variant="error"
      />
      <BaseButton :iconAfter="payButtonIcon" type="button" @click="onPayWithoutPayment">{{
        paymentButtonLabel
      }}</BaseButton>
    </div>
  </div>
</template>

<style scoped>
.donation-page {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    'gallery'
    'header'
    'widget'
    'details'
    'payment';
  gap: var(--space-1-5);

  @media (--desktop-experience) {
    grid-template-columns: minmax(0, 1fr) 420px;
    grid-template-areas:
      'gallery gallery'
      'header widget'
      'details widget'
      'payment widget';
    align-items: start;
    column-gap: var(--space-3);
    row-gap: var(--space-2);
  }

  .donation-page-gallery {
    grid-area: gallery;
  }

  .donation-page-header {
    grid-area: header;
  }

  .donation-page-widget {
    grid-area: widget;

    @media (--desktop-experience) {
      position: sticky;
      top: var(--space-2);
      right: 0;
    }
  }

  .donation-page-details {
    display: flex;
    flex-direction: column;
    gap: var(--space-1-5);

    @media (--desktop-experience) {
      grid-area: details;
      gap: var(--space-2);
      padding-top: var(--space-2);
      border-top: var(--divider);
    }
  }

  /* Nothing above the cart or payment block, so this line would stack on the next one. */
  @media (--desktop-experience) {
    &:not(:has(.donation-page-options)):not(:has(.donation-page-identity)) {
      .donation-page-details {
        padding-top: 0;
        border-top: none;
      }
    }

    &:not(:has(.donation-page-options)):not(:has(.donation-page-identity)):not(:has(.donation-page-cart-total)) {
      grid-template-areas:
        'gallery gallery'
        'header widget'
        'payment widget';

      .donation-page-details {
        display: none;
      }
    }
  }

  /* Options are omitted when the ticket group has no survey, so the line only follows that section. */
  .donation-page-options + :deep(.identity-form),
  .donation-page-options + :deep(.member-email-form),
  .donation-page-cart-total {
    border-top: var(--divider);
    padding-top: var(--space-1-5);

    @media (--desktop-experience) {
      padding-top: var(--space-2);
    }
  }

  .donation-page-payment-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    grid-area: payment;
    border-top: var(--divider);
    padding-top: var(--space-1-5);

    @media (--desktop-experience) {
      padding-top: var(--space-2);
    }
  }

  /* The fees block already opens payment information. */
  &:has(.donation-page-cart-total) .donation-page-payment-container {
    border-top: none;
    padding-top: 0;
  }
}
</style>
